import type { EventCardData } from '../components/events/eventCardTypes';
import { resolveApiUrl } from '../utils/subdomainUtils';
import { logger } from "../utils/logger";
import { resolveEventCardLocationLabel } from '../../shared/events/resolveEventCardLocationLabel';
import {
    coerceEventListingPlainText,
    firstNonEmptyListingText,
} from '../../shared/utils/coerceEventListingText';
import { resolveEventListingChipLabel } from '../../shared/events/resolveEventListingChipLabel';
import { EVENT_DESCRIPTION_PLACEHOLDER } from '../../shared/events/eventListingPlaceholders';
import { resolveEventPriceDisplay } from '../../shared/utils/trainingCost';
// import { logger } from "../utils/logger";
export interface EventsPagination {
    page: number;
    limit: number;
    total: number;
    pages: number;
}

export interface GetEventsPaginatedParams {
    page?: number;
    limit?: number;
    search?: string;
    state?: string;
    county_id?: number;
    category_id?: number;
    mode?: string;
    latitude?: number;
    longitude?: number;
    radius_miles?: number;
    free?: boolean;
    when?: 'upcoming' | 'past';
}

/**
 * UNIFIED EVENT SERVICE
 *
 * This ensures ALL pages get the EXACT same event data as the proper model
 * from https://event.americanpolicetraining.com:5173/
 *
 * ALL pages MUST use this service instead of their own APIs
 */
export class UnifiedEventService {
    /**
     * Get events with server-side pagination and filters.
     * Returns events and pagination info for the events listing page.
     */
    static async getEventsPaginated(params: GetEventsPaginatedParams = {}): Promise<{
        events: EventCardData[];
        pagination: EventsPagination;
    }> {
        const {
            page = 1,
            limit = 12,
            search,
            state,
            county_id,
            category_id,
            mode,
            latitude,
            longitude,
            radius_miles,
            free,
            when,
        } = params;
        try {
            const query = new URLSearchParams();
            query.set('page', String(page));
            query.set('limit', String(limit));
            if (search?.trim()) query.set('search', search.trim());
            if (state?.trim()) query.set('state', state.trim());
            if (county_id != null && Number.isFinite(county_id) && county_id > 0) {
                query.set('county_id', String(county_id));
            }
            if (category_id != null) query.set('category_id', String(category_id));
            if (mode?.trim()) query.set('mode', mode.trim());
            if (latitude != null && !Number.isNaN(latitude)) query.set('latitude', String(latitude));
            if (longitude != null && !Number.isNaN(longitude)) query.set('longitude', String(longitude));
            if (radius_miles != null && !Number.isNaN(radius_miles) && radius_miles > 0) query.set('radius_miles', String(radius_miles));
            if (free) query.set('free', '1');
            if (when === 'past') query.set('when', 'past');
            const url = resolveApiUrl(`/api/events?${query.toString()}`);
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            if (data.success && data.data?.events && data.data?.pagination) {
                return {
                    events: data.data.events.map((event: any) => this.mapToEventCardData(event)),
                    pagination: {
                        page: data.data.pagination.page,
                        limit: data.data.pagination.limit,
                        total: data.data.pagination.total,
                        pages: data.data.pagination.pages
                    }
                };
            }
            return {
                events: [],
                pagination: { page: 1, limit, total: 0, pages: 0 }
            };
        } catch (error) {
            logger.error('Error fetching events (paginated):', error, {});
            return {
                events: [],
                pagination: { page: 1, limit, total: 0, pages: 0 }
            };
        }
    }

    /**
     * Get events using the SAME API endpoint as the proper model
     * This ensures ALL pages get identical data
     */
    static async getEvents(limit: number = 50): Promise<EventCardData[]> {
        try {
            const url = resolveApiUrl(`/api/events?limit=${limit}`);
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            if (data.success && data.data && data.data.events) {
                // Transform using the EXACT same mapping as the proper model
                return data.data.events.map((event: any) => this.mapToEventCardData(event));
            }
            return [];
        }
        catch (error) {
            logger.error('Error fetching events:', error, {});
            return [];
        }
    }
    /**
     * Homepage upcoming section: random events starting within the next 30 days.
     * Falls back to the general public listing when the homepage pool is empty or the
     * homepage_upcoming query fails open (deploy/import regressions, transient API errors).
     */
    static async getHomepageUpcomingEvents(limit: number = 3): Promise<EventCardData[]> {
        const safeLimit = Math.max(1, Math.min(limit, 12));
        try {
            const url = resolveApiUrl(
                `/api/events?limit=${safeLimit}&homepage_upcoming=1`,
            );
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            const mapped = this.mapEventListFromApiPayload(data);
            if (mapped.length > 0) {
                return mapped.slice(0, safeLimit);
            }
        } catch (error) {
            logger.error('Error fetching homepage upcoming events:', error, {});
        }

        try {
            const fallback = await this.getEventsPaginated({
                page: 1,
                limit: safeLimit,
            });
            return fallback.events.slice(0, safeLimit);
        } catch (error) {
            logger.error('Error fetching homepage upcoming fallback events:', error, {});
            return [];
        }
    }

    private static mapEventListFromApiPayload(data: unknown): EventCardData[] {
        if (
            typeof data !== 'object' ||
            data === null ||
            !('success' in data) ||
            !(data as { success?: boolean }).success
        ) {
            return [];
        }
        const events = (data as { data?: { events?: unknown[] } }).data?.events;
        if (!Array.isArray(events)) {
            return [];
        }
        const mapped: EventCardData[] = [];
        for (const event of events) {
            try {
                mapped.push(this.mapToEventCardData(event));
            } catch (error) {
                logger.error('Error mapping homepage event card:', error, {
                    eventId: (event as { id?: unknown })?.id,
                });
            }
        }
        return mapped;
    }

    /**
     * Get featured events using the SAME API endpoint as the proper model
     */
    static async getFeaturedEvents(limit: number = 8): Promise<EventCardData[]> {
        try {
            const url = resolveApiUrl(`/api/events?limit=${limit}&featured=true`);
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            if (data.success && data.data && data.data.events) {
                // Transform using the EXACT same mapping as the proper model
                return data.data.events.map((event: any) => this.mapToEventCardData(event));
            }
            return [];
        }
        catch (error) {
            logger.error('Error fetching featured events:', error, {});
            return [];
        }
    }
    /**
     * Map API event data to EventCardData (EXACT same as proper model)
     */
    private static mapToEventCardData(event: any): EventCardData {
        logger.debug('🔍 UnifiedEventService mapping event:', { "arg0": {
                id: event.id,
                title: event.training_title,
                registration_open_date: event.registration_open_date,
                registration_close_date: event.registration_close_date
            } });
        const venueName = coerceEventListingPlainText(event.venue_name, '');
        const venueCity = coerceEventListingPlainText(event.venue_city, '');
        const venueState = coerceEventListingPlainText(event.venue_state, '');
        const venueAddress = coerceEventListingPlainText(
            event.venue_address || event.location_event_address,
            '',
        );
        const locationResolved = resolveEventCardLocationLabel({
            location: event.location,
            venueAddress,
            venueCity,
            venueState,
        });
        const locationLine =
            locationResolved === 'Location TBD' ? '' : locationResolved;
        return {
            id: event.id,
            title: firstNonEmptyListingText([event.training_title, event.title], 'Untitled Event'),
            description: firstNonEmptyListingText(
                [event.training_short_description, event.training_long_description, event.description],
                EVENT_DESCRIPTION_PLACEHOLDER,
            ),
            startDate: event.start_datetime || event.startDate || event.start_date || '',
            endDate: event.end_datetime || event.endDate || event.end_date || '',
            location: locationLine,
            featured: event.is_featured || false,
            imageUrl: (() => {
                const raw = event.main_image || event.imageUrl || '';
                const trimmed = typeof raw === 'string' ? raw.trim() : '';
                if (!trimmed || trimmed.includes('event-placeholder')) return '';
                return trimmed;
            })(),
            capacity: event.max_registrants || event.max_participants || event.capacity || 0,
            ...(() => {
                const display = resolveEventPriceDisplay({
                    min: event.min_training_cost,
                    max: event.max_training_cost,
                    legacyPrice: event.price,
                    title: firstNonEmptyListingText([event.training_title, event.title], ''),
                });
                if (display.kind === 'unknown') return { price: null as number | null };
                if (display.kind === 'range') {
                    return { price: display.low ?? null, priceHigh: display.high ?? null };
                }
                return { price: display.amount ?? 0 };
            })(),
            mode: coerceEventListingPlainText(event.mode, 'in-person'),
            category: resolveEventListingChipLabel({
                ticsDomainName: event.tics_domain_name ?? event.ticsDomainName,
                category: coerceEventListingPlainText(event.category, ''),
            }),
            ticsDomainName: event.tics_domain_name ?? event.ticsDomainName ?? null,
            totalTrainingHours: event.total_training_hours || event.credits,
            isEligibleForContinuingEducation: event.is_eligible_for_continuing_education || false,
            willBeReportedToCommission: event.will_be_reported_to_commission || false,
            stateCommissionId: event.state_commission_id,
            stateCommissionName: event.state_commission_name,
            stateCommissionCode: event.state_commission_code, // This will get "TCOLE" from database
            slug: coerceEventListingPlainText(event.slug, String(event.id)),
            registered_count: event.registered_count || 0,
            registration_open_date: event.registration_open_date,
            registration_close_date: event.registration_close_date,
            is_canceled: !!event.is_canceled,
            is_external: !!event.is_external,
            expressRegisterEligible:
                event.express_register_eligible === true ||
                event.expressRegisterEligible === true,
            venue: {
                name: venueName,
                address: venueAddress,
                city: venueCity,
                state: venueState,
            },
            latitude: event.latitude != null && !Number.isNaN(Number(event.latitude)) ? Number(event.latitude) : undefined,
            longitude: event.longitude != null && !Number.isNaN(Number(event.longitude)) ? Number(event.longitude) : undefined,
            hostAgencyName: event.host_agency_name ?? null,
            hostAgencySlug: event.host_agency_slug ?? null,
            hostAgencyImageUrl: event.host_agency_logo ?? event.hostAgencyImageUrl ?? null,
            aiCoverUrl: (() => {
                const raw = event.ai_cover_url ?? event.aiCoverUrl ?? '';
                const trimmed = typeof raw === 'string' ? raw.trim() : '';
                if (!trimmed || trimmed.includes('event-placeholder')) return null;
                return trimmed;
            })(),
            crawledHostedByAgency: event.crawled_hosted_by_agency ?? null,
            leadInstructorName: event.lead_instructor_name ?? null,
            pocDisplayName: event.poc_display_name ?? null,
            timezone: event.timezone ?? null,
        };
    }
}
